import React from 'react';
import type { HandlerId } from '../../../nodeTypes/components/IfElseCard/types';
import useCopyActionData from './hooks/useCopyActionData';
import usePastePreviewSync from './hooks/usePastePreviewSync';
import getEdgePosition from './utils/getEdgePosition';
import getIsIfElseOpenPathChoiceEdge from './utils/getIsIfElseOpenPathChoiceEdge';
import PreviewEdgeLabel from './components/PreviewEdgeLabel';
import ChooseEdgeLabel from './components/ChooseEdgeLabel';
import InsertEdgeLabel from './components/InsertEdgeLabel';

export type Props = {
  /** Edge source action id */
  sourceId: string;

  /** IfElse branch key for branch-specific edges */
  sourceKey?: HandlerId;

  /** Edge target action id */
  targetId: string;

  /** Source action's offset x */
  sourceX: number;

  /** Target action's offset x */
  targetX: number;

  /** Source action's offset y */
  sourceY: number;

  /** Target action's offset y */
  targetY: number;

  /** Callback to insert the copy pasted action */
  onInsertAction: () => void;
};

const PasteContainer: React.FCC<Props> = React.memo(
  ({
    onInsertAction,
    sourceId,
    sourceKey,
    targetId,
    sourceX,
    targetX,
    sourceY,
    targetY,
  }) => {
    const { midX, midY } = getEdgePosition({
      sourceX,
      sourceY,
      targetX,
      targetY,
    });

    const edgeKey = `${sourceId}__to__${targetId}`;

    const {
      isCopyActionInteraction,
      isChoosingPathToConnect,
      isPastePreviewEdge,
      isPastingPhase,
      lowestLevelIds,
      copiedActionById,
      copiedAction,
    } = useCopyActionData({ sourceId, targetId });

    usePastePreviewSync({
      isPastePreviewEdge,
      midX,
      midY,
      edgeKey,
    });

    if (!isCopyActionInteraction) return null;

    if (isChoosingPathToConnect) {
      const isIfElseOpenPathChoiceEdge = getIsIfElseOpenPathChoiceEdge({
        isChoosingPathToConnect,
        copiedAction,
        sourceKey,
        copiedActionById,
      });

      const isChoiceEdge =
        (sourceKey == null && lowestLevelIds.has(sourceId)) ||
        isIfElseOpenPathChoiceEdge;

      if (!isChoiceEdge && !isPastePreviewEdge) return null;

      if (isPastePreviewEdge) {
        return <PreviewEdgeLabel midX={midX} midY={midY} />;
      }

      const chooseLeafButtonTestId = sourceKey
        ? `chooseLeafButton-${sourceId}-${sourceKey}`
        : `chooseLeafButton-${sourceId}`;

      return (
        <ChooseEdgeLabel
          midX={midX}
          midY={midY}
          edgeKey={edgeKey}
          testId={chooseLeafButtonTestId}
          onInsertAction={onInsertAction}
        />
      );
    }

    if (isPastingPhase) {
      const testId = sourceKey
        ? `pasteContainer-${sourceId}-${sourceKey}`
        : `pasteContainer-${sourceId}`;

      return (
        <InsertEdgeLabel
          midX={midX}
          midY={midY}
          testId={testId}
          onInsertAction={onInsertAction}
        />
      );
    }

    return null;
  },
);

export default PasteContainer;
